import React, { useState } from 'react';
import { toast } from 'sonner';
import { ImageIcon, X } from 'lucide-react';
import IconPicker from '@/components/IconPicker';
import MediaPickerModal from '@/components/MediaPickerModal';

// Shared Publish modal used by the Vault list and the inline editor's "Save & Publish".
export default function PublishModal({ publication, categories, onClose, onSubmit, submitLabel = 'Publish Document' }) {
  const [pricingType, setPricingType] = useState(publication?.pricing_type || 'free');
  const [price, setPrice] = useState(publication?.price ? String(publication.price) : '');
  const [freeTrial, setFreeTrial] = useState(!!publication?.free_trial);
  const [tier, setTier] = useState(publication?.tier || 'basic');
  const [category, setCategory] = useState(publication?.category || '');
  const [description, setDescription] = useState(publication?.description || '');
  const [coverImageUrl, setCoverImageUrl] = useState(publication?.cover_image_url || '');
  const [displayIcon, setDisplayIcon] = useState(publication?.display_icon || 'BookOpen');
  const [showPicker, setShowPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const filteredCategories = categories.filter(cat => cat.tier === tier);

  const handleTierChange = (newTier) => {
    setTier(newTier);
    setCategory('');
  };

  const handleSubmit = async () => {
    if (!category) {
      toast.error('Please select a category');
      return;
    }
    if (pricingType === 'single_purchase' && (!price || parseFloat(price) <= 0)) {
      toast.error('Please enter a valid price');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        pricing_type: pricingType,
        price: pricingType === 'single_purchase' ? parseFloat(price) : 0,
        free_trial: freeTrial,
        tier,
        category,
        description,
        cover_image_url: coverImageUrl,
        display_icon: displayIcon
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="publish-modal">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-3xl font-bold text-[#6B4423] mb-6">Publish: {publication.title}</h2>

        <div className="space-y-4">
          {/* Pricing Type */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Pricing Type *</label>
            <select
              data-testid="publish-modal-pricing"
              value={pricingType}
              onChange={(e) => setPricingType(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            >
              <option value="free">Free</option>
              <option value="single_purchase">Single Purchase</option>
              <option value="subscription_only">Subscription Only</option>
            </select>
          </div>

          {/* Price (if single purchase) */}
          {pricingType === 'single_purchase' && (
            <div>
              <label className="block font-bold text-[#6B4423] mb-2">Price ($) *</label>
              <input
                data-testid="publish-modal-price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
                placeholder="9.99"
              />
            </div>
          )}

          {/* Free Trial Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="freeTrial"
              checked={freeTrial}
              onChange={(e) => setFreeTrial(e.target.checked)}
              className="w-5 h-5"
            />
            <label htmlFor="freeTrial" className="font-bold text-[#6B4423]">
              Enable Free Trial (temporarily free access)
            </label>
          </div>

          {/* Tier */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Tier * (select tier first)</label>
            <select
              data-testid="publish-modal-tier"
              value={tier}
              onChange={(e) => handleTierChange(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            >
              <option value="basic">Basic</option>
              <option value="comps_ready">Comps Ready</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Category - Filtered by Tier */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">
              Category * (showing {tier === 'basic' ? 'Basic' : tier === 'comps_ready' ? 'Comps Ready' : 'Advanced'} categories)
            </label>
            <select
              data-testid="publish-modal-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            >
              <option value="">Select category...</option>
              {filteredCategories.map((cat, idx) => (
                <option key={`pub-cat-${idx}-${cat.slug}`} value={cat.slug}>{cat.icon} {cat.name}</option>
              ))}
            </select>
            {filteredCategories.length === 0 && (
              <p className="text-sm text-red-600 mt-1">No categories available for this tier. Please add categories in Test Categories.</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Public Description</label>
            <textarea
              data-testid="publish-modal-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
              rows="4"
              placeholder="Brief description for the Publication Center..."
            />
          </div>

          {/* Cover Image */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Cover Image</label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                data-testid="publish-modal-choose-image"
                className="px-4 py-2 bg-[#1B4B5A] text-white rounded hover:bg-[#143a45] flex items-center gap-2"
              >
                <ImageIcon className="w-4 h-4" />
                {coverImageUrl ? 'Change Image' : 'Choose Image'}
              </button>
              {coverImageUrl && (
                <div className="mt-2 relative inline-block">
                  <img src={coverImageUrl} alt="Cover preview" className="w-32 h-32 object-cover rounded border-2 border-gray-300" />
                  <button
                    type="button"
                    onClick={() => setCoverImageUrl('')}
                    className="absolute -top-2 -right-2 bg-white rounded-full p-1 text-red-600 shadow border border-gray-200"
                    title="Remove cover"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Icon Picker */}
          <IconPicker
            selectedIcon={displayIcon}
            onSelectIcon={setDisplayIcon}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4 mt-6">
          <button
            data-testid="publish-modal-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-60"
          >
            {submitting ? 'Publishing...' : submitLabel}
          </button>
          <button
            data-testid="publish-modal-cancel"
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg font-bold hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>

      <MediaPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(url) => { setCoverImageUrl(url); setShowPicker(false); }}
      />
    </div>
  );
}
